import { Component, ElementRef, EventEmitter, HostListener, Output } from '@angular/core';
import { CalendarDay, DateRange, QuickRangeKey } from './date-range-filter.type';
import { isDateRangeLongerThanAllowed } from './date-range-filter.util';

@Component({
    selector: 'core-ui-date-range-filter',
    standalone: false,
    templateUrl: './date-range-filter.component.html',
    styleUrls: ['./date-range-filter.component.scss'],
})
export class DateRangeFilterComponent {
    @Output() rangeChange = new EventEmitter<DateRange>();

    isOpen = false;
    isCustomOpen = false;
    dropdownAlignRight = false;

    private ignoreNextDocDown = false;

    activeKey: QuickRangeKey = 'TODAY';
    displayValue = '';
    customStart?: string;
    customEnd?: string;

    hasRange = false;

    weekDays = ['D', 'L', 'M', 'MI', 'J', 'V', 'S'];

    leftMonthDays: CalendarDay[] = [];
    rightMonthDays: CalendarDay[] = [];

    private viewBaseDate: Date;

    quickOptions: { key: QuickRangeKey; label: string }[] = [
        { key: 'TODAY', label: 'Hoy' },
        { key: 'YESTERDAY', label: 'Ayer' },
        { key: 'LAST_7', label: 'Últimos 7 días' },
        { key: 'LAST_30', label: 'Últimos 30 días' },
        { key: 'THIS_MONTH', label: 'Este mes' },
        { key: 'PREV_MONTH', label: 'Mes anterior' },
        { key: 'CUSTOM', label: 'Rango personalizado' },
    ];

    private readonly minDate: Date;
    private readonly maxDate: Date;

    private readonly maxRangeDays = 30;

    constructor(private elementRef: ElementRef) {
        const today = this.stripTime(new Date());
        this.maxDate = today;
        this.minDate = this.addDays(today, -180);
        const initial = this.getRangeForKey('TODAY');
        this.applyRange(initial);

        this.viewBaseDate = today;
        this.buildCalendars();
    }

    @HostListener('document:mousedown', ['$event'])
    onDocMouseDown(ev: MouseEvent): void {
        if (!this.isOpen) return;
        if (this.ignoreNextDocDown) return;
        const t = ev.target as Node;
        const host = this.elementRef.nativeElement as HTMLElement;
        if (host.contains(t)) return;
        this.closeDropdown();
    }

    onPanelMouseDown(): void {
        this.ignoreNextDocDown = true;
        queueMicrotask(() => (this.ignoreNextDocDown = false));
    }

    private closeDropdown(): void {
        this.isOpen = false;
        this.isCustomOpen = false;
    }

    get canGoPrevMonth(): boolean {
        const newBase = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );
        const newLeftMonth = new Date(
            newBase.getFullYear(),
            newBase.getMonth() - 1,
            1
        );
        const minMonth = new Date(
            this.minDate.getFullYear(),
            this.minDate.getMonth(),
            1
        );

        return newLeftMonth >= minMonth;
    }

    get canGoNextMonth(): boolean {
        const newBase = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() + 1,
            1
        );
        const newRightMonth = newBase;
        const currentMonth = new Date(
            this.maxDate.getFullYear(),
            this.maxDate.getMonth(),
            1
        );

        return newRightMonth <= currentMonth;
    }

    private buildCalendars(): void {
        const right = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth(),
            1
        );

        const left = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );

        this.leftMonthDays = this.buildMonthDays(left);
        this.rightMonthDays = this.buildMonthDays(right);
    }

    get leftMonthLabel(): string {
        const left = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );
        return this.getMonthName(left.getMonth()) + ' ' + left.getFullYear();
    }

    get rightMonthLabel(): string {
        const right = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth(),
            1
        );
        return this.getMonthName(right.getMonth()) + ' ' + right.getFullYear();
    }

    private buildMonthDays(monthDate: Date): CalendarDay[] {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();

        const firstOfMonth = new Date(year, month, 1);
        const startWeekDay = firstOfMonth.getDay();

        const startDate = new Date(year, month, 1 - startWeekDay);

        const days: CalendarDay[] = [];
        for (let i = 0; i < 42; i++) {
            const current = new Date(startDate);
            current.setDate(startDate.getDate() + i);
            days.push({
                date: this.stripTime(current),
                label: current.getDate(),
                isOutside: current.getMonth() !== month,
            });
        }
        return days;
    }

    private getMonthName(m: number): string {
        const names = [
            'Enero', 'Febrero', 'Marzo', 'Abril',
            'Mayo', 'Junio', 'Julio', 'Agosto',
            'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
        ];
        return names[m];
    }

    onPrevMonth(): void {
        if (!this.canGoPrevMonth) return;

        this.viewBaseDate = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() - 1,
            1
        );
        this.buildCalendars();
    }

    onNextMonth(): void {
        if (!this.canGoNextMonth) return;

        this.viewBaseDate = new Date(
            this.viewBaseDate.getFullYear(),
            this.viewBaseDate.getMonth() + 1,
            1
        );
        this.buildCalendars();
    }

    isDisabledDay(d: Date): boolean {
        const date = this.stripTime(d);
        const today = this.stripTime(new Date());

        if (date > today) {
            return true;
        }

        if (date < this.minDate) {
            return true;
        }

        if (this.customStart && !this.customEnd) {
            const start = this.parseInputDate(this.customStart);

            if (isDateRangeLongerThanAllowed(date, start, this.maxRangeDays)) {
                return true;
            }
        }

        return false;
    }

    isOutOfRangeWindow(d: Date): boolean {
        if (!this.customStart || this.customEnd) return false;

        const date = this.stripTime(d);
        const start = this.parseInputDate(this.customStart);

        return isDateRangeLongerThanAllowed(date, start, this.maxRangeDays);
    }

    onSelectDate(date: Date): void {
        const clicked = this.stripTime(date);

        if (this.isDisabledDay(clicked)) {
            return;
        }

        const iso = this.toInputDateValue(clicked);

        if (!this.customStart || (this.customStart && this.customEnd)) {
            this.customStart = iso;
            this.customEnd = undefined;
        } else {
            const start = this.parseInputDate(this.customStart);
            const end = clicked;

            if (isDateRangeLongerThanAllowed(end, start, this.maxRangeDays)) {
                return;
            }

            if (end < start) {
                this.customEnd = this.customStart;
                this.customStart = this.toInputDateValue(end);
            } else {
                this.customEnd = iso;
            }
        }

        this.hasRange = !!(this.customStart && this.customEnd);

        if (this.customStart && this.customEnd) {
            const s = this.parseInputDate(this.customStart);
            const e = this.parseInputDate(this.customEnd);
            this.displayValue = this.formatRange(s, e);
        }
    }

    private parseInputDate(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    isStart(d: Date): boolean {
        if (!this.customStart) return false;
        const s = this.parseInputDate(this.customStart);
        return this.isSameDate(d, s);
    }

    isEnd(d: Date): boolean {
        if (!this.customEnd) return false;
        const e = this.parseInputDate(this.customEnd);
        return this.isSameDate(d, e);
    }

    isInRange(d: Date): boolean {
        if (!this.customStart || !this.customEnd) return false;

        const s = this.parseInputDate(this.customStart);
        const e = this.parseInputDate(this.customEnd);
        const time = this.stripTime(d).getTime();
        return time > s.getTime() && time < e.getTime();
    }

    private isSameDate(a: Date, b: Date): boolean {
        return (
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate()
        );
    }

    toggleDropdown(): void {
        this.isOpen = !this.isOpen;
        if (!this.isOpen) {
            this.isCustomOpen = false;
        } else {
            const host = this.elementRef.nativeElement as HTMLElement;
            const rect = host.getBoundingClientRect();
            const dropdownWidth = Math.min(560, window.innerWidth - 16);
            this.dropdownAlignRight = rect.left + dropdownWidth > window.innerWidth - 8;
        }
    }

    onSelectQuickOption(option: QuickRangeKey): void {
        this.activeKey = option;

        if (option === 'CUSTOM') {
            this.isCustomOpen = true;
            const today = new Date();
            if (!this.customStart) {
                this.customStart = this.toInputDateValue(today);
            }
            if (!this.customEnd) {
                this.customEnd = this.toInputDateValue(today);
            }
            return;
        }

        this.isCustomOpen = false;
        const range = this.getRangeForKey(option);
        this.applyRange(range);
        this.isOpen = false;
    }

    onApplyCustom(): void {
        // Con solo una fecha marcada (selección a medias o un solo día),
        // aplicar la trata como rango de un día en vez de ignorar el clic.
        const only = this.customStart ?? this.customEnd;
        if (!only) return;
        this.customStart = this.customStart ?? only;
        this.customEnd = this.customEnd ?? only;

        const start = this.parseInputDate(this.customStart);
        const end = this.parseInputDate(this.customEnd);

        const [s, e] = start <= end ? [start, end] : [end, start];
        this.customStart = this.toInputDateValue(s);
        this.customEnd = this.toInputDateValue(e);
        this.hasRange = true;

        const range: DateRange = {
            start: s,
            end: e,
            key: 'CUSTOM',
            label: 'Rango personalizado',
        };

        this.applyRange(range);
        this.isCustomOpen = false;
        this.isOpen = false;
    }

    onCancelCustom(): void {
        this.isCustomOpen = false;
    }

    private getRangeForKey(key: QuickRangeKey): DateRange {
        const today = this.stripTime(new Date());

        switch (key) {
            case 'TODAY': {
                return {
                    start: today,
                    end: today,
                    key,
                    label: 'Hoy',
                };
            }
            case 'YESTERDAY': {
                const y = this.addDays(today, -1);
                return {
                    start: y,
                    end: y,
                    key,
                    label: 'Ayer',
                };
            }
            case 'LAST_7': {
                const end = this.addDays(today, -1);
                const start = this.addDays(end, -6);
                return {
                    start,
                    end,
                    key,
                    label: 'Últimos 7 días',
                };
            }
            case 'LAST_30': {
                const end = this.addDays(today, -1);
                const start = this.addDays(end, -29);
                return {
                    start,
                    end,
                    key,
                    label: 'Últimos 30 días',
                };
            }
            case 'THIS_MONTH': {
                const start = new Date(today.getFullYear(), today.getMonth(), 1);
                const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                // No pedir días futuros: el mes corre solo hasta hoy.
                const end = monthEnd > today ? today : monthEnd;
                return {
                    start,
                    end,
                    key,
                    label: 'Este mes',
                };
            }
            case 'PREV_MONTH': {
                const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const end = new Date(today.getFullYear(), today.getMonth(), 0);
                return {
                    start,
                    end,
                    key,
                    label: 'Mes anterior',
                };
            }
            case 'CUSTOM':
            default: {
                const start = today;
                const end = today;
                return {
                    start,
                    end,
                    key: 'CUSTOM',
                    label: 'Rango personalizado',
                };
            }
        }
    }

    private applyRange(range: DateRange): void {
        this.activeKey = range.key;
        this.displayValue = this.formatRange(range.start, range.end);
        this.rangeChange.emit(range);
    }

    private stripTime(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    private addDays(d: Date, days: number): Date {
        const result = new Date(d);
        result.setDate(result.getDate() + days);
        return this.stripTime(result);
    }

    private formatRange(start: Date, end: Date): string {
        const s = this.formatDate(start);
        const e = this.formatDate(end);
        if (s === e) return s;
        return `${s} - ${e}`;
    }

    private formatDate(d: Date): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}/${mm}/${yyyy}`;
    }

    private toInputDateValue(d: Date): string {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${yyyy}-${mm}-${dd}`;
    }
}
